import React, { useState, useEffect } from 'react';
import { getProduct, getProductIntegrations } from '../../services/api';
import { Product, IntegrationField } from '../../types';
import { X, Edit, Database, Code, ChevronDown, ChevronUp, Tag, Server, Activity, FileJson, Settings, ExternalLink } from 'lucide-react';
import ContentLoader from '../ui/ContentLoader';
import ProductIntegrationStatus from './ProductIntegrationStatus';
import ProductEditModal from './ProductEditModal';
import { usePermissions } from '../../hooks/usePermissions';

interface ProductDetailModalProps {
  productId: string;
  isOpen?: boolean;
  onClose: () => void;
  onEdit?: (productId: string) => void;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  productId,
  isOpen = true,
  onClose,
  onEdit
}) => {
  const { canManageProducts } = usePermissions();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [configSectionOpen, setConfigSectionOpen] = useState(true);
  const [soarSectionOpen, setSoarSectionOpen] = useState(true);
  const [loggerConfigOpen, setLoggerConfigOpen] = useState(true);
  const [keywordsOpen, setKeywordsOpen] = useState(true);
  const [integratedTenants, setIntegratedTenants] = useState<any[]>([]);
  const [isIntegrationsModalOpen, setIsIntegrationsModalOpen] = useState(false);
  
  // Add backdrop effect to body when modal opens
  useEffect(() => {
    if (isOpen) {
      // Add backdrop blur class to body
      document.body.classList.add('modal-open');
      // Disable scrolling on the body
      document.body.style.overflow = 'hidden';
    } else {
      // Remove backdrop blur class from body
      document.body.classList.remove('modal-open');
      // Re-enable scrolling
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !productId) return;
    
    const loadProduct = async () => {
      setIsLoading(true);
      try {
        const data = await getProduct(productId);
        setProduct(data);
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [productId, isOpen]);

  // Fetch integrated tenants and aggregate keywords
  useEffect(() => {
    const fetchIntegratedTenants = async () => {
      if (!productId || !product) return;
      
      try {
        // Fetch actual integrations for this product
        const integrations = await getProductIntegrations(productId);
        
        if (integrations && integrations.length > 0) {
          // Extract the tenant information from the integrations
          const tenants = integrations.map(integration => integration.customer || integration);
          setIntegratedTenants(tenants);
          
          // For logger products, aggregate keywords from all integrations
          if (product.type === 'logger' && product.logger_config) {
            // Collect all unique keywords from integrations
            const allKeywords: any[] = [];
            const keywordMap = new Map<string, any>();
            
            // Start with product-level keywords
            if (product.logger_config.keywords && Array.isArray(product.logger_config.keywords)) {
              product.logger_config.keywords.forEach((kw: any) => {
                const key = kw.keyword || JSON.stringify(kw);
                if (!keywordMap.has(key)) {
                  keywordMap.set(key, kw);
                  allKeywords.push(kw);
                }
              });
            }
            
            // Add customer-specific keywords from integrations
            integrations.forEach((integration: any) => {
              if (integration.keywords && Array.isArray(integration.keywords)) {
                integration.keywords.forEach((kw: any) => {
                  const key = kw.keyword || JSON.stringify(kw);
                  if (!keywordMap.has(key)) {
                    keywordMap.set(key, kw);
                    allKeywords.push(kw);
                  }
                });
              }
            });
            
            // Update product with aggregated keywords
            if (allKeywords.length > 0) {
              setProduct({
                ...product,
                logger_config: {
                  ...product.logger_config,
                  keywords: allKeywords
                }
              });
            }
          }
        } else {
          setIntegratedTenants([]);
        }
      } catch (error) {
        console.error('Error fetching integrated tenants:', error);
        setIntegratedTenants([]);
      }
    };
    
    if (product) {
      fetchIntegratedTenants();
    }
  }, [productId, product]);

  // Handle edit modal
  const handleOpenEditModal = () => {
    if (onEdit) {
      onEdit(productId);
    } else {
    setIsEditModalOpen(true);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  // Reload product data after save
  const handleProductSaved = async () => {
    if (!productId) return;
    
    setIsLoading(true);
    try {
      const data = await getProduct(productId);
      setProduct(data);
    } catch (err) {
      console.error('Error reloading product:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Separate integration fields by type
  const getRegularIntegrationFields = (fields?: IntegrationField[]) => {
    return fields?.filter(field => field.type !== 'soar-mapping') || [];
  };

  const getSoarMappingFields = (fields?: IntegrationField[]) => {
    return fields?.filter(field => field.type === 'soar-mapping') || [];
  };

  const toggleConfigSection = () => {
    setConfigSectionOpen(!configSectionOpen);
  };

  const toggleSoarSection = () => {
    setSoarSectionOpen(!soarSectionOpen);
  };
  
  const toggleLoggerConfigSection = () => {
    setLoggerConfigOpen(!loggerConfigOpen);
  };
  
  const toggleKeywordsSection = () => {
    setKeywordsOpen(!keywordsOpen);
  };

  // Helper function to check if a tag is a predefined tag
  const isPredefinedTag = (key: string) => {
    return ['productName', 'productModule'].includes(key);
  };

  // Helper function to get custom tags
  const getCustomTags = (tags?: Record<string, any>) => {
    if (!tags) return {};
    
    const customTags: Record<string, any> = {};
    Object.entries(tags).forEach(([key, value]) => {
      if (!isPredefinedTag(key) && value) {
        customTags[key] = value;
      }
    });
    
    return customTags;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-hidden">
      <div className="bg-background-card rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto animate-modalOpen scroll-smooth">
        {/* Header with simple styling */}
        <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
          <div className="flex items-center">
            {product && product.logo_url ? (
              <img 
                src={product.logo_url} 
                alt={product.name}
                className="h-12 w-12 mr-4 object-contain bg-white rounded p-1"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '';
                  (e.target as HTMLImageElement).classList.add('hidden');
                }}
              />
            ) : product ? (
              <div className="h-12 w-12 mr-4 flex items-center justify-center bg-background-hover rounded">
                <span className="text-lg font-bold text-text-muted-dark">
                  {product.name.substring(0, 2).toUpperCase()}
                </span>
              </div>
            ) : null}
            <div>
              <h2 className="text-xl font-bold text-white">
                {product ? product.name : 'Product Details'}
              </h2>
              <div className="text-sm text-gray-400 flex items-center flex-wrap gap-1 mt-1">
                {product && product.tags?.productModule && (
                  <span className="bg-primary-700/20 text-primary-300 px-2 py-0.5 rounded text-xs">
                    {product.tags.productModule}
                  </span>
                )}
                {product && product.type && (
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    product.type === 'logger' 
                      ? 'bg-green-700/20 text-green-300' 
                      : 'bg-blue-700/20 text-blue-300'
                  }`}>
                    {product.type === 'logger' ? 'Logger' : 'SaaS'}
                  </span>
                )}
                {product && product.type !== 'logger' && (
                  <span className="bg-background-hover text-gray-300 px-2 py-0.5 rounded text-xs">
                    {product.integration_fields?.length ?? 0} field{(product.integration_fields?.length ?? 0) !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            {product && canManageProducts() && (
              <button 
                onClick={handleOpenEditModal}
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/20 px-3 py-1.5 rounded-lg flex items-center transition-all duration-200"
              >
                <Edit size={14} className="mr-1" />
                Edit
              </button>
            )}
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/10 backdrop-blur-sm text-white hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <ContentLoader
              message="Loading product details..."
              size="lg"
              withCard={false}
              minHeight="300px"
            />
          ) : error || !product ? (
            <div className="flex flex-col items-center justify-center py-8">
              <h2 className="text-xl font-semibold mb-2">{error || 'Product not found'}</h2>
              <button 
                onClick={onClose} 
                className="premium-button-primary mt-4"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Product Information and Integration Status in the top row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Left column - Basic info */}
                <div className="card lg:col-span-1">
                  <h2 className="text-lg font-medium mb-4">Product Information</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm text-gray-500">Product Name</h4>
                      <p>{product.name}</p>
                    </div>
                    
                    {/* Product type specific info */}
                    {product.type === 'logger' ? (
                      <>
                        <div>
                          <h4 className="text-sm text-gray-500">Product Type</h4>
                          <p>{product.tags?.productType || 'N/A'}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm text-gray-500">Log Type</h4>
                          <p>{product.tags?.logType || 'N/A'}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Predefined tags for SaaS products */}
                    {product.tags?.productName && (
                      <div>
                        <h4 className="text-sm text-gray-500">Product Name Tag</h4>
                        <p>{product.tags.productName}</p>
                      </div>
                    )}
                    
                    {product.tags?.productModule && (
                      <div>
                        <h4 className="text-sm text-gray-500">Product Module Tag</h4>
                        <p>{product.tags.productModule}</p>
                      </div>
                        )}
                      </>
                    )}

                    {/* Custom tags section */}
                    {product.tags && Object.keys(getCustomTags(product.tags)).length > 0 && (
                      <div>
                        <h4 className="text-sm text-gray-500 mb-2 flex items-center">
                          <Tag size={14} className="mr-1" />
                          Custom Tags
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(getCustomTags(product.tags)).map(([key, value]) => (
                            <div 
                              key={key} 
                              className="bg-background-hover border border-border-dark rounded px-2 py-1 text-xs flex items-center"
                            >
                              <span className="text-gray-400 mr-1">{key}:</span>
                              <span>{value as string}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Right column - Integration Status */}
                <div className="lg:col-span-2">
                  {integratedTenants.length > 0 ? (
                    <div className="p-4 border border-border-dark rounded-lg">
                      <h3 className="text-lg font-medium mb-4">Integration Status</h3>
                      <div className="p-3 bg-blue-600/10 border border-blue-600/20 rounded-md">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                          <span className="font-medium text-blue-400">Integrated</span>
                          <span className="ml-auto text-xs text-gray-400">
                            {integratedTenants.length} {integratedTenants.length === 1 ? 'customer' : 'customers'} 
                          </span>
                        </div>
                        <p className="text-sm mt-2">
                          This product is successfully integrated with:
                        </p>
                        <ul className="mt-2 space-y-1">
                          {/* Show first 3 customers directly */}
                          {integratedTenants.slice(0, 3).map((tenant, index) => {
                            const tenantId = (tenant as any).id || (tenant as any)._id;
                            const tenantName = (tenant as any).name || `Customer ${tenantId?.substring(0, 8) || index + 1}`;
                            const tenantType = (tenant as any).tenant_type || 'Unknown';
                            const tenantLevel = (tenant as any).level || '';
                            const isIntegrationActive = (tenant as any).active !== false; // Assume active unless explicitly set to false
                            
                            return (
                              <li key={tenantId || index} className="flex items-center">
                                <div className="w-1 h-1 bg-status-success rounded-full mr-2"></div>
                                <span className="font-medium">{tenantName}</span>
                              </li>
                            );
                          })}
                          
                          {/* If there are more than 3 customers, show "View All" option */}
                          {integratedTenants.length > 3 && (
                            <li className="mt-1 pt-1 border-t border-border-dark">
                              <button 
                                className="text-sm text-primary hover:underline focus:outline-none"
                                onClick={() => setIsIntegrationsModalOpen(true)}
                              >
                                View all {integratedTenants.length} integrated customers
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border border-border-dark rounded-lg">
                      <h3 className="text-lg font-medium mb-4">Integration Status</h3>
                      <div className="p-3 bg-status-warning/10 border border-status-warning/20 rounded-md">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-status-warning rounded-full mr-2"></div>
                          <span className="font-medium text-status-warning">Not Integrated</span>
                        </div>
                        <p className="text-sm mt-2">
                          This product is not yet integrated with any customer.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {product.type === 'logger' ? (
                // Logger-specific sections
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Logger Configuration */}
                  <div className="card">
                    <div 
                      className="flex items-center justify-between mb-4 cursor-pointer"
                      onClick={toggleLoggerConfigSection}
                    >
                      <div className="flex items-center">
                        <Server size={18} className="text-green-500 mr-2" />
                        <h2 className="text-lg font-medium">Logger Configuration</h2>
                      </div>
                      <div>
                        {loggerConfigOpen ? (
                          <ChevronUp size={18} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={18} className="text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {loggerConfigOpen && product.logger_config && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm text-gray-500">Template Name</h4>
                            <p>{product.logger_config.template_name}</p>
                          </div>
                          
                          <div>
                            <h4 className="text-sm text-gray-500">Filename</h4>
                            <p>{product.logger_config.filename}</p>
                          </div>
                          
                          <div>
                            <h4 className="text-sm text-gray-500">Queue Size</h4>
                            <p>{product.logger_config.queue_size}</p>
                          </div>
                          
                          <div>
                            <h4 className="text-sm text-gray-500">Max Disk Space</h4>
                            <p>{product.logger_config.max_disk_space} MB</p>
                          </div>
                          
                          <div>
                            <h4 className="text-sm text-gray-500">Worker Threads</h4>
                            <p>{product.logger_config.worker_threads}</p>
                          </div>
                          
                          <div>
                            <h4 className="text-sm text-gray-500">Status</h4>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              product.logger_config.status === 'active' 
                                ? 'bg-green-700/20 text-green-300' 
                                : 'bg-status-danger/20 text-status-danger'
                            }`}>
                              {product.logger_config.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Keywords */}
                  <div className="card">
                    <div 
                      className="flex items-center justify-between mb-4 cursor-pointer"
                      onClick={toggleKeywordsSection}
                    >
                      <div className="flex items-center">
                        <Activity size={18} className="text-yellow-500 mr-2" />
                        <h2 className="text-lg font-medium">Keywords</h2>
                      </div>
                      <div className="flex items-center">
                        <span className="px-2 py-1 bg-yellow-700/20 text-yellow-300 rounded-full text-xs mr-2">
                          {product.logger_config?.keywords?.length || 0} keyword{(product.logger_config?.keywords?.length || 0) !== 1 ? 's' : ''}
                        </span>
                        {keywordsOpen ? (
                          <ChevronUp size={18} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={18} className="text-gray-400" />
                        )}
                      </div>
                    </div>
                    
                    {keywordsOpen && (
                      product.logger_config?.keywords?.length === 0 ? (
                        <div className="py-8 text-center text-text-muted-dark">
                          <p>No keywords defined for this logger product.</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                          {product.logger_config?.keywords?.map((keyword, index) => (
                            <div 
                              key={index}
                              className="p-3 bg-card-dark border border-border-dark rounded-lg flex justify-between items-center"
                            >
                              <div className="text-yellow-300 font-mono">{keyword.keyword}</div>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                keyword.condition === 'AND' 
                                  ? 'bg-blue-700/20 text-blue-300' 
                                  : 'bg-orange-700/20 text-orange-300'
                              }`}>
                                {keyword.condition}
                              </span>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                  
                  {/* Config File Preview */}
                  <div className="card lg:col-span-2">
                    <div className="flex items-center mb-4">
                      <FileJson size={18} className="text-blue-500 mr-2" />
                      <h2 className="text-lg font-medium">Config File Generation</h2>
                    </div>
                    
                    <p className="text-text-muted-dark mb-4">
                      This logger product can be used to generate configuration files for your logging system.
                      To generate a configuration file, add this product to a logger customer.
                    </p>
                    
                    <div className="flex justify-center">
                      <button 
                        className="premium-button-secondary"
                        onClick={onClose}
                      >
                        Go to Logger Customers
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // SaaS product sections - Configuration Fields and SOAR Mapping side by side
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Configuration Fields */}
                <div className="card">
                  <div 
                    className="flex items-center justify-between mb-4 cursor-pointer"
                    onClick={toggleConfigSection}
                  >
                    <div className="flex items-center">
                      <Database size={18} className="text-primary mr-2" />
                      <h2 className="text-lg font-medium">Configuration Fields</h2>
                    </div>
                    <div className="flex items-center">
                      <span className="px-2 py-1 bg-background-hover rounded-full text-xs mr-2">
                        {getRegularIntegrationFields(product.integration_fields).length} field{getRegularIntegrationFields(product.integration_fields).length !== 1 ? 's' : ''}
                      </span>
                      {configSectionOpen ? (
                        <ChevronUp size={18} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={18} className="text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {configSectionOpen && (
                    getRegularIntegrationFields(product.integration_fields).length === 0 ? (
                      <div className="py-8 text-center text-text-muted-dark">
                        <p>No configuration fields defined for this product.</p>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                        {getRegularIntegrationFields(product.integration_fields).map((field: IntegrationField) => (
                          <div 
                            key={field.field_key}
                            className="p-4 bg-card-dark border border-border-dark rounded-lg"
                          >
                            <div className="flex items-center mb-2">
                              <h3 className="font-medium">{field.label}</h3>
                              {field.is_required && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary-700/30 text-primary-300 rounded">
                                  Required
                                </span>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Field Key:</span>
                                <code className="px-2 py-0.5 rounded text-xs text-white">

                                  {field.field_key}
                                </code>
                              </div>
                              
                              <div>
                                <span className="text-gray-500">Type:</span>
                                <span className="ml-2">
                                  {field.type === 'text' && 'Text Input'}
                                  {field.type === 'number' && 'Number Input'}
                                  {field.type === 'secret' && 'Secured Password'}
                                  {field.type === 'url' && 'URL Address'}
                                  {field.type === 'multi-select' && 'Multi-Select'}
                                </span>
                              </div>
                              
                              {field.default_value && (
                                <div className="md:col-span-2">
                                  <span className="text-gray-500">Default Value:</span>
                                  <span className="ml-2">
                                    {field.type === 'secret' ? '••••••••' : field.default_value}
                                  </span>
                                </div>
                              )}

                              {field.type === 'multi-select' && field.options && (
                                <div className="md:col-span-2">
                                  <span className="text-gray-500">Options:</span>
                                  <div className="ml-2 mt-1 flex flex-wrap gap-1">
                                    {field.options.map(option => (
                                      <span key={option} className="px-2 py-0.5 bg-background-hover rounded text-xs">
                                        {option}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>

                {/* SOAR Mapping Fields */}
                <div className="card">
                  <div 
                    className="flex items-center justify-between mb-4 cursor-pointer"
                    onClick={toggleSoarSection}
                  >
                    <div className="flex items-center">
                      <Code size={18} className="text-blue-500 mr-2" />
                      <h2 className="text-lg font-medium">SOAR Field Mapping</h2>
                    </div>
                    <div className="flex items-center">
                      <span className="px-2 py-1 bg-blue-700/30 text-blue-300 rounded-full text-xs mr-2">
                        {getSoarMappingFields(product.integration_fields).length} mapping{getSoarMappingFields(product.integration_fields).length !== 1 ? 's' : ''}
                      </span>
                      {soarSectionOpen ? (
                        <ChevronUp size={18} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={18} className="text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {soarSectionOpen && (
                    getSoarMappingFields(product.integration_fields).length === 0 ? (
                      <div className="py-8 text-center text-text-muted-dark">
                        <p>No SOAR field mappings defined for this product.</p>
                      </div>
                    ) : (
                      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                        {getSoarMappingFields(product.integration_fields).map((field: IntegrationField) => (
                          <div 
                            key={field.field_key}
                            className="p-4 bg-card-dark border border-border-dark rounded-lg"
                          >
                            <div className="flex items-center mb-2">
                              <h3 className="font-medium">{field.label}</h3>
                              {field.is_required && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-700/30 text-blue-300 rounded">
                                  Required
                                </span>
                              )}
                            </div>
                            
                            {/* Field Key and Type information hidden as requested */}
                            <div className="grid grid-cols-1 gap-4 text-sm">
                              {field.field_mapping && Object.keys(field.field_mapping).length > 0 && (
                                <div className="md:col-span-2 mt-2">
                                  <span className="text-gray-500 mb-2 block">Field Mapping:</span>
                                  <div className="bg-background-hover p-3 rounded-md">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr>
                                          <th className="text-left py-1 px-2 text-gray-400">Log Field</th>
                                          <th className="text-left py-1 px-2 text-gray-400">SOAR Field</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {Object.entries(field.field_mapping).map(([key, value]) => (
                                          <tr key={key} className="border-t border-border-dark">
                                            <td className="py-1 px-2 text-green-400">{value}</td>
                                            <td className="py-1 px-2 text-blue-400">{key}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Product Edit Modal */}
      {productId && (
        <ProductEditModal
          productId={productId}
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSaved={handleProductSaved}
        />
      )}

      {/* Integrations Modal */}
      {isIntegrationsModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
          <div className="bg-background-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-modalOpen">
            <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
              <h2 className="text-xl font-bold">Integrated Customers</h2>
              <button 
                onClick={() => setIsIntegrationsModalOpen(false)}
                className="p-2 rounded-full hover:bg-white/10 text-white hover:text-white transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-sm text-gray-400 mb-4">
                The following customers are integrated with {product?.name || 'this product'}:
              </p>
              
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {integratedTenants.map((tenant, index) => {
                  const tenantId = (tenant as any).id || (tenant as any)._id;
                  const tenantName = (tenant as any).name || `Customer ${tenantId?.substring(0, 8) || index + 1}`;
                  const tenantType = (tenant as any).tenant_type || 'Unknown';
                  const tenantLevel = (tenant as any).level || '';
                  const isIntegrationActive = (tenant as any).active !== false; // Assume active unless explicitly set to false
                  
                  return (
                    <div 
                      key={tenantId || index} 
                      className="p-3 bg-card-dark border border-border-dark rounded-lg flex items-center"
                    >
                      <div className="flex-grow">
                        <div className="flex items-center">
                          <span className="font-medium">{tenantName}</span>
                        </div>
                        <div className="text-xs text-gray-400 flex items-center gap-2 mt-1">
                          {tenantLevel && (
                            <span className="px-1.5 py-0.5 bg-primary-700/20 text-primary-300 rounded">
                              {tenantLevel}
                            </span>
                          )}
                          <span>{tenantType}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 ${isIntegrationActive ? 'bg-green-500' : 'bg-red-500'} rounded-full`} title={`Integration ${isIntegrationActive ? 'Active' : 'Inactive'}`}></div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Navigate directly to the edit integration page for this tenant and product
                            window.location.href = `/integrations?tenant=${tenantId}&product=${productId}&edit=true`;
                          }}
                          className="p-2 bg-primary/20 hover:bg-primary/30 text-primary-300 rounded-full transition-colors"
                          title="Edit Integration"
                        >
                          <ExternalLink size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {integratedTenants.length === 0 && (
                <div className="p-4 text-center text-gray-400">
                  No customers are integrated with this product.
                </div>
              )}
              
              <div className="flex justify-end mt-4 pt-2 border-t border-border-dark">
                <button
                  onClick={() => setIsIntegrationsModalOpen(false)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailModal; 